import { useEffect, useRef } from 'react';
import styles from './RotatingGlobe.module.css';
import worldRaw from './world-countries-110m.json';

interface GlobeNode {
  /** Latitude in degrees (-90 = south pole, 90 = north pole) */
  lat: number;
  /** Longitude in degrees (-180..180) */
  lon: number;
  /** Pulse phase offset in 0..1 of the pulse period */
  phase?: number;
}

interface GlobeConnection {
  from: number;
  to: number;
}

const NODES: GlobeNode[] = [
  // North America
  { lat: 40.71, lon: -74.0, phase: 0.0 },     // New York
  { lat: 34.05, lon: -118.25, phase: 0.18 },  // Los Angeles
  { lat: 41.88, lon: -87.63, phase: 0.35 },   // Chicago
  { lat: 25.76, lon: -80.19, phase: 0.62 },   // Miami
  { lat: 43.65, lon: -79.38, phase: 0.08 },   // Toronto
  { lat: 49.28, lon: -123.12, phase: 0.5 },   // Vancouver
  { lat: 19.43, lon: -99.13, phase: 0.7 },    // Mexico City
  // South America
  { lat: 4.71, lon: -74.07, phase: 0.22 },    // Bogotá
  { lat: -12.05, lon: -77.04, phase: 0.4 },   // Lima
  { lat: -22.91, lon: -43.17, phase: 0.78 },  // Rio de Janeiro
  { lat: -23.55, lon: -46.63, phase: 0.5 },   // São Paulo
  { lat: -34.6, lon: -58.38, phase: 0.13 },   // Buenos Aires
  // Europe
  { lat: 51.5, lon: -0.13, phase: 0.4 },      // London
  { lat: 48.85, lon: 2.35, phase: 0.55 },     // Paris
  { lat: 52.52, lon: 13.4, phase: 0.92 },     // Berlin
  { lat: 40.42, lon: -3.7, phase: 0.27 },     // Madrid
  { lat: 41.9, lon: 12.5, phase: 0.6 },       // Rome
  { lat: 59.33, lon: 18.07, phase: 0.05 },    // Stockholm
  { lat: 41.01, lon: 28.98, phase: 0.71 },    // Istanbul
  { lat: 55.75, lon: 37.62, phase: 0.15 },    // Moscow
  // Africa & Middle East
  { lat: 30.04, lon: 31.24, phase: 0.33 },    // Cairo
  { lat: 6.52, lon: 3.38, phase: 0.88 },      // Lagos
  { lat: -1.29, lon: 36.82, phase: 0.45 },    // Nairobi
  { lat: -26.2, lon: 28.04, phase: 0.65 },    // Johannesburg
  { lat: -33.93, lon: 18.42, phase: 0.18 },   // Cape Town
  { lat: 25.2, lon: 55.27, phase: 0.85 },     // Dubai
  // Asia
  { lat: 19.08, lon: 72.88, phase: 0.1 },     // Mumbai
  { lat: 28.7, lon: 77.1, phase: 0.95 },      // Delhi
  { lat: 13.75, lon: 100.5, phase: 0.36 },    // Bangkok
  { lat: 1.35, lon: 103.82, phase: 0.3 },     // Singapore
  { lat: -6.21, lon: 106.85, phase: 0.55 },   // Jakarta
  { lat: 14.6, lon: 120.98, phase: 0.82 },    // Manila
  { lat: 22.3, lon: 114.17, phase: 0.42 },    // Hong Kong
  { lat: 31.23, lon: 121.47, phase: 0.74 },   // Shanghai
  { lat: 39.9, lon: 116.4, phase: 0.18 },     // Beijing
  { lat: 37.57, lon: 126.98, phase: 0.5 },    // Seoul
  { lat: 35.68, lon: 139.7, phase: 0.65 },    // Tokyo
  // Oceania
  { lat: -33.87, lon: 151.21, phase: 0.8 },   // Sydney
  { lat: -36.85, lon: 174.76, phase: 0.4 },   // Auckland
];

const CONNECTIONS: GlobeConnection[] = [
  // Trans-Atlantic
  { from: 0, to: 12 },   // NY ↔ London
  { from: 0, to: 13 },   // NY ↔ Paris
  { from: 4, to: 12 },   // Toronto ↔ London
  // Domestic NA
  { from: 0, to: 1 },    // NY ↔ LA
  { from: 0, to: 2 },    // NY ↔ Chicago
  { from: 1, to: 5 },    // LA ↔ Vancouver
  { from: 0, to: 3 },    // NY ↔ Miami
  { from: 1, to: 6 },    // LA ↔ Mexico City
  // Americas
  { from: 6, to: 10 },   // Mexico City ↔ São Paulo
  { from: 7, to: 8 },    // Bogotá ↔ Lima
  { from: 9, to: 11 },   // Rio ↔ Buenos Aires
  { from: 3, to: 7 },    // Miami ↔ Bogotá
  // Europe
  { from: 12, to: 13 },  // London ↔ Paris
  { from: 12, to: 14 },  // London ↔ Berlin
  { from: 14, to: 19 },  // Berlin ↔ Moscow
  { from: 13, to: 15 },  // Paris ↔ Madrid
  { from: 17, to: 19 },  // Stockholm ↔ Moscow
  { from: 18, to: 25 },  // Istanbul ↔ Dubai
  // Africa
  { from: 21, to: 20 },  // Lagos ↔ Cairo
  { from: 22, to: 23 },  // Nairobi ↔ Johannesburg
  { from: 23, to: 24 },  // Johannesburg ↔ Cape Town
  { from: 22, to: 25 },  // Nairobi ↔ Dubai
  // Asia
  { from: 25, to: 26 },  // Dubai ↔ Mumbai
  { from: 26, to: 27 },  // Mumbai ↔ Delhi
  { from: 28, to: 29 },  // Bangkok ↔ Singapore
  { from: 29, to: 30 },  // Singapore ↔ Jakarta
  { from: 32, to: 33 },  // Hong Kong ↔ Shanghai
  { from: 33, to: 34 },  // Shanghai ↔ Beijing
  { from: 34, to: 35 },  // Beijing ↔ Seoul
  { from: 35, to: 36 },  // Seoul ↔ Tokyo
  { from: 36, to: 32 },  // Tokyo ↔ Hong Kong
  // Trans-Pacific
  { from: 1, to: 36 },   // LA ↔ Tokyo
  { from: 1, to: 37 },   // LA ↔ Sydney
  // Oceania
  { from: 37, to: 38 },  // Sydney ↔ Auckland
  { from: 36, to: 37 },  // Tokyo ↔ Sydney
  // Cross-continent
  { from: 12, to: 25 },  // London ↔ Dubai
  { from: 25, to: 29 },  // Dubai ↔ Singapore
];

interface ProjectedPoint {
  x: number;
  y: number;
  /** Z in unit-sphere coords. > 0 means front-of-sphere (visible). */
  z: number;
}

// ---------- Topology decoding (TopoJSON → array of [lon,lat] rings) ----------

interface TopoArcPoint extends Array<number> {}
interface TopoTransform {
  scale: [number, number];
  translate: [number, number];
}
interface TopoPolygonGeom {
  type: 'Polygon';
  arcs: number[][];
}
interface TopoMultiPolygonGeom {
  type: 'MultiPolygon';
  arcs: number[][][];
}
type TopoGeom = TopoPolygonGeom | TopoMultiPolygonGeom;
interface Topology {
  transform: TopoTransform;
  arcs: TopoArcPoint[][];
  objects: { countries: { geometries: TopoGeom[] } };
}

/**
 * Minimal TopoJSON decoder: walks the country geometries, dequantizes each
 * referenced arc, and stitches them into closed rings of [lon, lat]. Negative
 * arc indices are bitwise-inverted and traversed in reverse, per the spec.
 */
function decodeWorldRings(topo: Topology): number[][][] {
  const { scale, translate } = topo.transform;

  const decodeArc = (idx: number): number[][] => {
    const reverse = idx < 0;
    const arc = topo.arcs[reverse ? ~idx : idx];
    let x = 0;
    let y = 0;
    const pts: number[][] = new Array(arc.length);
    for (let i = 0; i < arc.length; i++) {
      x += arc[i][0];
      y += arc[i][1];
      pts[i] = [x * scale[0] + translate[0], y * scale[1] + translate[1]];
    }
    if (reverse) pts.reverse();
    return pts;
  };

  const stitchRing = (arcIndices: number[]): number[][] => {
    const ring: number[][] = [];
    for (let i = 0; i < arcIndices.length; i++) {
      const seg = decodeArc(arcIndices[i]);
      // After the first arc in a ring, the first point of each subsequent arc
      // duplicates the previous arc's last point, so skip it.
      if (i === 0) {
        for (const p of seg) ring.push(p);
      } else {
        for (let j = 1; j < seg.length; j++) ring.push(seg[j]);
      }
    }
    return ring;
  };

  const out: number[][][] = [];
  for (const geom of topo.objects.countries.geometries) {
    if (geom.type === 'Polygon') {
      for (const arcs of geom.arcs) out.push(stitchRing(arcs));
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.arcs) {
        for (const arcs of poly) out.push(stitchRing(arcs));
      }
    }
  }
  return out;
}

const COUNTRY_RINGS = decodeWorldRings(worldRaw as unknown as Topology);

// ---------------------------------------------------------------------------

export default function RotatingGlobe() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let cssW = 0;
    let cssH = 0;

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === cssW && h === cssH) return;
      cssW = w;
      cssH = h;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    let rafId = 0;
    let running = true;
    const start = performance.now();
    // Earth-axis-style tilt: pitch the north pole toward the viewer,
    // then roll the whole sphere clockwise so the equator runs diagonally
    // (matches the reference renders).
    const pitch = (-23 * Math.PI) / 180;
    const cosP = Math.cos(pitch);
    const sinP = Math.sin(pitch);
    const roll = (-16 * Math.PI) / 180;
    const cosR = Math.cos(roll);
    const sinR = Math.sin(roll);

    const project = (
      latDeg: number,
      lonDeg: number,
      yaw: number,
      radius: number,
      cx: number,
      cy: number
    ): ProjectedPoint => {
      const la = (latDeg * Math.PI) / 180;
      const lo = (lonDeg * Math.PI) / 180 + yaw;
      const x = Math.cos(la) * Math.cos(lo);
      const y = Math.sin(la);
      const z = Math.cos(la) * Math.sin(lo);
      // Pitch around the X axis (lean north pole toward viewer).
      const yp = y * cosP - z * sinP;
      const zp = y * sinP + z * cosP;
      // Roll around the Z axis (screen-facing) so the equator slants.
      const xr = x * cosR - yp * sinR;
      const yr = x * sinR + yp * cosR;
      return { x: cx + xr * radius, y: cy - yr * radius, z: zp };
    };

    const drawWirePath = (points: ProjectedPoint[]) => {
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        const avgZ = (a.z + b.z) * 0.5;
        if (avgZ < -0.02) continue;
        const alpha = 0.03 + 0.13 * Math.max(0, avgZ);
        ctx.strokeStyle = `rgba(242, 115, 181, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    };

    const draw = (now: number) => {
      if (!running) return;
      const t = (now - start) / 1000;
      const w = cssW;
      const h = cssH;
      const cx = w / 2;
      const cy = h / 2;
      // Sphere radius is sized so radius * haloFactor stays inside the
      // canvas (half of the shorter side) — otherwise the halo gets clipped
      // by the canvas edge and shows a hard line.
      const haloFactor = 1.36;
      const radius = (Math.min(w, h) * 0.5 * 0.96) / haloFactor;

      ctx.clearRect(0, 0, w, h);

      const yaw = reduceMotion ? 0 : (t * 2 * Math.PI) / 60; // 60s per revolution

      // ---- Outer glow halo ----
      const halo = ctx.createRadialGradient(cx, cy, radius * 0.88, cx, cy, radius * haloFactor);
      halo.addColorStop(0, 'rgba(237, 53, 148, 0.28)');
      halo.addColorStop(0.55, 'rgba(237, 53, 148, 0.09)');
      halo.addColorStop(1, 'rgba(237, 53, 148, 0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * haloFactor, 0, Math.PI * 2);
      ctx.fill();

      // ---- Sphere body (dark with subtle inner color) ----
      const body = ctx.createRadialGradient(
        cx - radius * 0.35,
        cy - radius * 0.35,
        radius * 0.1,
        cx,
        cy,
        radius
      );
      body.addColorStop(0, 'rgba(78, 10, 42, 0.55)');
      body.addColorStop(0.6, 'rgba(22, 2, 10, 0.85)');
      body.addColorStop(1, 'rgba(0, 0, 0, 0.95)');
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();

      // ---- Wireframe (latitudes + meridians, very faint) ----
      ctx.lineWidth = 1;
      const samples = 64;
      for (const lat of [-60, -30, 0, 30, 60]) {
        const pts: ProjectedPoint[] = [];
        for (let i = 0; i <= samples; i++) {
          const lon = (i / samples) * 360 - 180;
          pts.push(project(lat, lon, yaw, radius, cx, cy));
        }
        drawWirePath(pts);
      }
      for (let m = 0; m < 12; m++) {
        const lon = m * 30;
        const pts: ProjectedPoint[] = [];
        for (let i = 0; i <= samples; i++) {
          const lat = (i / samples) * 180 - 90;
          pts.push(project(lat, lon, yaw, radius, cx, cy));
        }
        drawWirePath(pts);
      }

      // ---- Country outlines ----
      // Build a single Path2D of all visible (front-face) segments and stroke
      // it once. Crossings to the back of the sphere are clipped out segment
      // by segment so the outline neatly disappears at the limb.
      const countryPath = new Path2D();
      for (const ring of COUNTRY_RINGS) {
        let prev: ProjectedPoint | null = null;
        let inSegment = false;
        for (let i = 0; i < ring.length; i++) {
          const lon = ring[i][0];
          const lat = ring[i][1];
          const p = project(lat, lon, yaw, radius, cx, cy);
          if (prev) {
            const visible = prev.z >= 0 && p.z >= 0;
            if (visible) {
              if (!inSegment) {
                countryPath.moveTo(prev.x, prev.y);
                inSegment = true;
              }
              countryPath.lineTo(p.x, p.y);
            } else {
              inSegment = false;
            }
          }
          prev = p;
        }
      }
      ctx.lineWidth = 0.7;
      ctx.strokeStyle = 'rgba(242, 115, 181, 0.55)';
      ctx.stroke(countryPath);

      // ---- Great-circle arcs between connected nodes ----
      const arcSamples = 36;
      for (const conn of CONNECTIONS) {
        const a = NODES[conn.from];
        const b = NODES[conn.to];
        const aLa = (a.lat * Math.PI) / 180;
        const aLo = (a.lon * Math.PI) / 180;
        const bLa = (b.lat * Math.PI) / 180;
        const bLo = (b.lon * Math.PI) / 180;
        const ax = Math.cos(aLa) * Math.cos(aLo);
        const ay = Math.sin(aLa);
        const az = Math.cos(aLa) * Math.sin(aLo);
        const bx = Math.cos(bLa) * Math.cos(bLo);
        const by = Math.sin(bLa);
        const bz = Math.cos(bLa) * Math.sin(bLo);
        const dot = Math.max(-1, Math.min(1, ax * bx + ay * by + az * bz));
        const omega = Math.acos(dot);
        const sinO = Math.sin(omega);
        if (sinO < 1e-4) continue;

        const pts: ProjectedPoint[] = new Array(arcSamples + 1);
        for (let i = 0; i <= arcSamples; i++) {
          const tt = i / arcSamples;
          const ka = Math.sin((1 - tt) * omega) / sinO;
          const kb = Math.sin(tt * omega) / sinO;
          const px = ka * ax + kb * bx;
          const py = ka * ay + kb * by;
          const pz = ka * az + kb * bz;
          const la = (Math.asin(py) * 180) / Math.PI;
          const lo = (Math.atan2(pz, px) * 180) / Math.PI;
          pts[i] = project(la, lo, yaw, radius, cx, cy);
        }
        ctx.lineWidth = 1.6;
        for (let i = 0; i < pts.length - 1; i++) {
          const p = pts[i];
          const q = pts[i + 1];
          const avgZ = (p.z + q.z) * 0.5;
          if (avgZ < -0.02) continue;
          const alpha = 0.22 + 0.6 * Math.max(0, avgZ);
          ctx.strokeStyle = `rgba(242, 115, 181, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(q.x, q.y);
          ctx.stroke();
        }
      }

      // ---- Pulsing nodes (front-face only) ----
      const period = 2.6;
      for (const node of NODES) {
        const p = project(node.lat, node.lon, yaw, radius, cx, cy);
        if (p.z < -0.02) continue;
        const phase = (node.phase ?? 0) * period;
        const tt = (((t + phase) % period) + period) % period;
        const k = tt / period;
        const visible = Math.max(0, p.z);
        const visAlpha = 0.6 + 0.4 * visible;

        // Expanding pulse ring (fades as it grows)
        const pulseR = 5 + 26 * k;
        const pulseAlpha = 0.55 * (1 - k) * visAlpha;
        ctx.fillStyle = `rgba(237, 53, 148, ${pulseAlpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, pulseR, 0, Math.PI * 2);
        ctx.fill();

        // Static halo
        const halo2 = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 12);
        halo2.addColorStop(0, `rgba(242, 115, 181, ${0.85 * visAlpha})`);
        halo2.addColorStop(1, 'rgba(242, 115, 181, 0)');
        ctx.fillStyle = halo2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 12, 0, Math.PI * 2);
        ctx.fill();

        // Bright core dot
        ctx.fillStyle = `rgba(255, 240, 247, ${visAlpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3.2, 0, Math.PI * 2);
        ctx.fill();
      }

      // ---- Sphere edge highlight ----
      const edge = ctx.createRadialGradient(cx, cy, radius * 0.96, cx, cy, radius * 1.02);
      edge.addColorStop(0, 'rgba(237, 53, 148, 0)');
      edge.addColorStop(0.5, 'rgba(237, 53, 148, 0.55)');
      edge.addColorStop(1, 'rgba(237, 53, 148, 0)');
      ctx.fillStyle = edge;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 1.02, 0, Math.PI * 2);
      ctx.fill();

      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.container}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        role="img"
        aria-label="Rotating globe with country outlines and pulsing city nodes"
      />
    </div>
  );
}
